import axios from 'axios';

// Ducks-style redux module for Template service
// Endpoints assumed (adjust if backend differs):
//  - GET  /Template/GetAll/{accountId}
//  - GET  /Template/GetSimple/{accountId}
//  - GET  /Template/Get/{id}
//  - POST /Template/Create
//  - PUT  /Template/Update/{id}
//  - DELETE /Template/Delete/{id}
//  - POST /Template/Synchronize

const initialState = {
    templates: [],
    simpleTemplates: [],
    currentTemplate: null,
};

// Action Types
const LIST_TEMPLATES = 'LIST_TEMPLATES';
const LIST_SIMPLE_TEMPLATES = 'LIST_SIMPLE_TEMPLATES';
const READ_TEMPLATE = 'READ_TEMPLATE';
const CREATE_TEMPLATE = 'CREATE_TEMPLATE';
const UPDATE_TEMPLATE = 'UPDATE_TEMPLATE';
const DELETE_TEMPLATE = 'DELETE_TEMPLATE';
const SYNCHRONIZE_TEMPLATES = 'SYNCHRONIZE_TEMPLATES';
const SET_LOADING = 'SET_LOADING';
const SET_ERROR = 'SET_ERROR';

// Reducer
export default function TemplateReducer(state = initialState, action) {
    switch (action.type) {
        case LIST_TEMPLATES:
            return { ...state, templates: action.payload };
        case LIST_SIMPLE_TEMPLATES:
            return { ...state, simpleTemplates: action.payload };
        case READ_TEMPLATE:
            return { ...state, currentTemplate: action.payload };
        case CREATE_TEMPLATE:
            return { ...state, templates: [...state.templates, action.payload] };
        case UPDATE_TEMPLATE:
            return {
                ...state,
                templates: state.templates.map(t => (t.id === action.payload.id ? { ...t, ...action.payload } : t)),
                currentTemplate: state.currentTemplate && state.currentTemplate.id === action.payload.id ? { ...state.currentTemplate, ...action.payload } : state.currentTemplate,
            };
        case DELETE_TEMPLATE:
            return { ...state, templates: state.templates.filter(t => t.id !== action.payload), currentTemplate: state.currentTemplate && state.currentTemplate.id === action.payload ? null : state.currentTemplate };
        case SYNCHRONIZE_TEMPLATES:
            // payload is expected to be the refreshed list
            return { ...state, templates: action.payload };
        default:
            return state;
    }
}

// Action Creators
export const SetLoading = (loading) => ({ type: SET_LOADING, payload: loading });
export const SetError = (error) => ({ type: SET_ERROR, payload: error });
export const SetTemplates = (templates) => ({ type: LIST_TEMPLATES, payload: templates });
export const SetSimpleTemplates = (templates) => ({ type: LIST_SIMPLE_TEMPLATES, payload: templates });
export const SetCurrentTemplate = (template) => ({ type: READ_TEMPLATE, payload: template });
export const AddTemplate = (template) => ({ type: CREATE_TEMPLATE, payload: template });
export const ModifyTemplate = (template) => ({ type: UPDATE_TEMPLATE, payload: template });
export const RemoveTemplate = (id) => ({ type: DELETE_TEMPLATE, payload: id });
export const SetSynchronizedTemplates = (templates) => ({ type: SYNCHRONIZE_TEMPLATES, payload: templates });

// Thunks / API calls
export const listTemplatesAPI = (accountId) => async (dispatch) => {
    try {
        const response = await axios.get(window.API_BASE_URL + '/Templates/GetAll/'+accountId);
        dispatch(SetTemplates(response.data));
    } catch (error) {
        console.error('Error listing templates:', error);
    }
};

export const readTemplateAPI = (id) => async (dispatch) => {
    try {
        const response = await axios.get(window.API_BASE_URL + '/Templates/Get/' + id);
        dispatch(SetCurrentTemplate(response.data));
    } catch (error) {
        console.error('Error reading template:', error);
    }
};

export const createTemplateAPI = (templateData) => async (dispatch) => {
    try {
        dispatch(SetLoading(true));
        
        const response = await axios.post(`${window.API_BASE_URL}/Templates/Create`, templateData);
        
        if (response.data) {
            dispatch(AddTemplate(response.data));
            return response.data;
        } else {
            // Si no hay data específica, refrescar la lista completa
            await dispatch(listTemplatesAPI(templateData.accountId));
        }
    } catch (error) {
        console.error('Error creating template:', error);
        const errorMessage = error.response?.data?.message || 
                           error.response?.data || 
                           'Error al crear la plantilla';
        dispatch(SetError(errorMessage));
        throw error;
    }
};

export const updateTemplateAPI = (id, updateData) => async (dispatch) => {
    try {
        dispatch(SetLoading(true));
        
        const response = await axios.put(`${window.API_BASE_URL}/Templates/Update/${id}`, updateData);
        
        if (response.data && response.data.template) {
            dispatch(ModifyTemplate(response.data.template));
            return response.data;
        } else {
            // Si no hay data específica, obtener la plantilla actualizada
            await dispatch(readTemplateAPI(id));
        }
    } catch (error) {
        console.error('Error updating template:', error);
        const errorMessage = error.response?.data?.message || 
                           error.response?.data || 
                           'Error al actualizar la plantilla';
        dispatch(SetError(errorMessage));
        throw error;
    }
};

export const deleteTemplateAPI = (id) => async (dispatch) => {
    try {
        dispatch(SetLoading(true));
        
        await axios.delete(`${window.API_BASE_URL}/Templates/Delete/${id}`);
        dispatch(RemoveTemplate(id));
    } catch (error) {
        console.error('Error deleting template:', error);
        const errorMessage = error.response?.data?.message || 
                           error.response?.data || 
                           'Error al eliminar la plantilla';
        dispatch(SetError(errorMessage));
        throw error;
    }
};

export const deleteTemplateByNameAPI = (accountNumber, templateName) => async (dispatch) => {
    try {
        dispatch(SetLoading(true));
        
        await axios.delete(`${window.API_BASE_URL}/Templates/Delete/${accountNumber}/${templateName}`);
        
        // Refrescar la lista de plantillas después de eliminar
        await dispatch(listTemplatesAPI(accountNumber));
    } catch (error) {
        console.error('Error deleting template by name:', error);
        const errorMessage = error.response?.data?.message || 
                           error.response?.data || 
                           'Error al eliminar la plantilla';
        dispatch(SetError(errorMessage));
        throw error;
    }
};

export const synchronizeTemplatesAPI = (accountId) => async (dispatch) => {
    try {
        dispatch(SetLoading(true));
        
        const response = await axios.get(`${window.API_BASE_URL}/Templates/Synchronize/${accountId}`);
        
        if (response.data) {
            // Después de sincronizar, obtener todas las plantillas actualizadas
            await dispatch(listTemplatesAPI(accountId));
        }
    } catch (error) {
        console.error('Error synchronizing templates:', error);
        const errorMessage = error.response?.data?.message || 
                           error.response?.data || 
                           'Error al sincronizar las plantillas';
        dispatch(SetError(errorMessage));
        throw error;
    }
};

export const listSimpleTemplatesAPI = (accountId) => async (dispatch) => {
    try {
        const response = await axios.get(window.API_BASE_URL + '/Templates/GetSimple/'+accountId);
        dispatch(SetSimpleTemplates(response.data));
    } catch (error) {
        console.error('Error listing templates:', error);
    }
};
